import React, { useEffect, useState } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import { NumericFormat } from 'react-number-format';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import store, { ruleActions } from '@apply/adminStore';
import { useSelector } from 'react-redux';
import { InformModal } from '../../common/SharedComponents';
import { BfiCustomStore, hasPermission } from '@apply/ui';
import { ExperienceApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { RefreshEvents } from '@apply/ui';
import { SupplementalRuleIdentifierTypeOptions } from '../common/constants/SupplementalRuleIdentifierTypeOptions';
import { SupplementalRuleChangeHistoryGrid } from '../common/components/SupplementalRuleChangeHistoryGrid';
import { SupplementalRuleChangeReasonModal } from './SupplementalRuleChangeReasonModal';
import { LoadPanel } from 'devextreme-react';
import { PermissionType } from '@apply/constants';
import { handleAdminError, handleAdminSuccess } from '../../common/SharedFunctions';

const styles = {
    buttonMarginRight: { marginRight: '10px' },
    buttonContainer: { marginRight: '20px', textAlign: 'right' },
    form: { paddingLeft: '5px', paddingTop: '10px' },
    formRow: { marginBottom: '0' },
    parent: {
        position: 'relative',
        weight: '750px',
    },
    cover: {
        backgroundColor: 'rgba(0,0,0,0.4)',
        position: 'absolute',
        height: '100%',
        width: '100%',
        display: 'block',
        zIndex: '999',
        textAlign: 'center',
        cursor: 'wait',
    },
    messageBox: {
        backgroundColor: 'white',
        display: 'inline-block',
        width: '65%',
        height: '100px',
        marginTop: '200px',
        padding: '20px',
        borderRadius: '10px',
        cursor: 'wait',
    },
    addButton: { height: '38px', float: 'left', marginLeft: '5px' },
    sectionHeader: { margin: '5px 10px' },
};

const SupplementalRuleModal = () => {
    const { isOpen, supplementalRuleId, onSave } = useSelector((state) => state.rules.supplementalruleModal);
    const { dispatch } = store;
    const [supplementalrule, set_supplementalrule] = useState(null);

    const canEdit = hasPermission(PermissionType.ExperienceEditor);

    const [supplementalRuleIdentifierTypeId, set_supplementalRuleIdentifierTypeId] = useState('');
    const [questionText, set_questionText] = useState('');
    const [credits, set_credits] = useState(null);

    const [loadingMessage, set_loadingMessage] = useState('Loading...');
    const [openNamesNotUnique, set_openNamesNotUnique] = useState(false);
    const [changesModalOpen, set_changesModalOpen] = useState(false);

    const experienceApi = new ExperienceApi.ExperienceApi(applicationInsights);
    const refreshEvents = new RefreshEvents();
    const historyStore = new BfiCustomStore();

    const _handleClose = () =>
        dispatch(
            ruleActions.setSupplementalRuleModal({
                supplementalruleModal: { isOpen: false },
            })
        );

    const _validateSubmission = async (e) => {
        e.preventDefault();

        if (!canEdit) {
            handleAdminWarning('You do not have rights to edit this information');
            return;
        }

        const hasChanges = hasOutstandingRuleChanges();
        if (hasChanges) {
            //Display modal and allow user to enters reason for change
            set_changesModalOpen(true);
            return;
        }
    };

    const hasOutstandingRuleChanges = () => {
        let hasChanges = false;

        hasChanges = supplementalrule.questionText !== questionText || supplementalrule.credits !== credits;

        if (hasChanges) {
            return true;
        }

        return hasChanges;
    };

    const _saveSupplementalRule = async (changeModel) => {
        try {
            set_loadingMessage('Saving...');

            const model = {
                id: supplementalRuleId,
                supplementalRuleIdentifierTypeId,
                questionText,
                credits,
                changeSummary: changeModel,
            };

            const response = await experienceApi.addUpdateSupplementalRule(model);
            if (response.statusCode !== 200) {
                return;
            }

            const savedRule = response.result;
            if (savedRule.id <= 0) {
                throw new Error('Failed to save new Supplemental Rule!');
            }
            refreshEvents.dispatchRefreshEvent();

            handleAdminSuccess('Supplemental Rule successfully saved.');

            if (onSave) {
                onSave(savedRule.id);
            }

            setTimeout(() => {
                dispatch(
                    ruleActions.setSupplementalRuleModal({
                        supplementalruleModal: { isOpen: false },
                    })
                );
            }, 800);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        } finally {
            set_loadingMessage('');
        }
    };

    const resetState = () => {
        set_supplementalrule(null);
        set_supplementalRuleIdentifierTypeId('');
        set_questionText('');
        set_credits(null);
    };

    useEffect(() => {
        async function isOpenChanged() {
            try {
                resetState();
                set_loadingMessage('Loading...');

                if (!isOpen) {
                    return;
                }

                let supplementalrule = null;
                if (supplementalRuleId && supplementalRuleId > 0) {
                    const response = await experienceApi.getSupplementalRule(supplementalRuleId);
                    if (response.statusCode !== 200) {
                        return;
                    }
                    supplementalrule = response.result;
                }

                if (!supplementalrule) {
                    supplementalrule = {
                        supplementalRuleIdentifierTypeId: '',
                        questionText: '',
                        credits: '',
                    };
                }
                const clone = JSON.parse(JSON.stringify(supplementalrule));
                set_supplementalrule(clone);

                set_supplementalRuleIdentifierTypeId(supplementalrule.supplementalRuleIdentifierTypeId ?? '');
                set_questionText(supplementalrule.questionText ?? '');
                set_credits(supplementalrule.credits ?? '');
            } catch (ex) {
                handleAdminError(ex, 'Failed to load Supplemental Rules! Please try again.');
            } finally {
                set_loadingMessage('');
            }
        }

        isOpenChanged();
    }, [isOpen, supplementalRuleId]);

    const onChangesModalCancel = () => {
        set_changesModalOpen(false);
    };

    const onChangesModalSubmit = async (model) => {
        set_changesModalOpen(false);
        await _saveSupplementalRule(model);
    };

    return (
        <Dialog
            maxWidth='sm'
            open={isOpen}
            onClose={_handleClose}
            aria-labelledby='form-dialog-title'
        >
            <DialogContent>
                <div className='modal-container'>
                    <div className='modal-backdrop'></div>
                    <div
                        className='modal'
                        tabIndex='-1'
                        role='dialog'
                        style={{ width: '700px' }}
                    >
                        <div
                            className='modal-dialog'
                            role='document'
                        >
                            <div style={styles.parent}>
                                <form
                                    name='ruleForm'
                                    onSubmit={(event) => _validateSubmission(event)}
                                >
                                    <div className='scrollable-content'>
                                        <div
                                            className='page-header'
                                            role='banner'
                                        >
                                            <h1>Experience Supplemental Rule</h1>
                                            <button
                                                type='button'
                                                className='close'
                                                data-dismiss='modal'
                                                aria-label='Close'
                                                onClick={_handleClose}
                                            >
                                                <span aria-hidden='true'>&times;</span>
                                            </button>
                                        </div>
                                        <div
                                            style={styles.form}
                                            className={'standard-form'}
                                        >
                                            <div
                                                className='row'
                                                style={styles.formRow}
                                            >
                                                <div className='columns small-12 large-6'>
                                                    <label htmlFor='supplementalRuleIdentifierTypeId'>
                                                        Supplemental Rule Identifier
                                                        <span className='req'>*</span>
                                                    </label>
                                                    <select
                                                        id='supplementalRuleIdentifierTypeId'
                                                        value={supplementalRuleIdentifierTypeId}
                                                        disabled={true}
                                                        onChange={(event) =>
                                                            set_supplementalRuleIdentifierTypeId(event.target.value)
                                                        }
                                                        required
                                                    >
                                                        <option value=''>Select</option>
                                                        {SupplementalRuleIdentifierTypeOptions.map((type) => {
                                                            return (
                                                                <option
                                                                    key={type.id}
                                                                    value={type.id}
                                                                >
                                                                    {type.name}
                                                                </option>
                                                            );
                                                        })}
                                                    </select>
                                                </div>
                                                <div className='columns small-12 large-6'>
                                                    <label htmlFor='credits'>
                                                        Credits
                                                        <span className='req'>*</span>
                                                    </label>
                                                    <NumericFormat
                                                        id='credits'
                                                        onValueChange={(values) => {
                                                            set_credits(values.floatValue);
                                                        }}
                                                        decimalScale={2}
                                                        allowNegative={false}
                                                        value={credits}
                                                        required
                                                        displayType='input'
                                                        isAllowed={(values, sourceInfo) => {
                                                            const value = values?.floatValue;
                                                            return value === undefined || value === null || value >= 0;
                                                        }}
                                                        disabled={!canEdit}
                                                    />
                                                </div>
                                            </div>
                                            <div
                                                className='row'
                                                style={styles.formRow}
                                            >
                                                <div className='columns small-12 large-12'>
                                                    <label htmlFor='questionText'>
                                                        Question Text
                                                        <span className='req'>*</span>
                                                    </label>
                                                    <textarea
                                                        name='questionText'
                                                        cols={40}
                                                        rows={3}
                                                        className='req'
                                                        value={questionText}
                                                        onChange={(event) => set_questionText(event.target.value)}
                                                        required
                                                        disabled={!canEdit}
                                                    />
                                                </div>
                                            </div>
                                            <div
                                                style={{
                                                    padding: '0px 10px 0 10px',
                                                }}
                                            >
                                                <SupplementalRuleChangeHistoryGrid
                                                    supplementalRuleId={supplementalRuleId}
                                                    gridStore={historyStore}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div
                                        className='button-footer'
                                        style={styles.buttonContainer}
                                    >
                                        <button
                                            style={styles.buttonMarginRight}
                                            type='submit'
                                            className='button primary'
                                            disabled={!canEdit}
                                        >
                                            Submit
                                        </button>
                                        <button
                                            type='button'
                                            className='button hollow'
                                            data-dismiss='modal'
                                            onClick={_handleClose}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                                <SupplementalRuleChangeReasonModal
                                    isOpen={changesModalOpen}
                                    onCancel={onChangesModalCancel}
                                    onSubmit={onChangesModalSubmit}
                                />
                                <InformModal
                                    isOpen={openNamesNotUnique}
                                    title='Duplicate Supplemental Rule!'
                                    text='The Supplemental Rule name that you have entered already exists in the database.'
                                    confirmCallback={() => set_openNamesNotUnique(false)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </DialogContent>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={!!loadingMessage}
                message={loadingMessage}
            />
        </Dialog>
    );
};

export default SupplementalRuleModal;
